import { useState, useEffect } from "react";
import { UserInfo } from "../../../types/user";
import { useMutation } from "react-query";
import UserService from "../../../services/userService";
import PhoneNumberSection from "./PhoneNumberSection";
import { useTranslation } from "react-i18next";
import { UiH4 } from "../../../components/text/UiTitle";

interface MyinfoProps {
  userInfo?: UserInfo;
  onChangePassword: () => void;
}

const Myinfo: React.FC<MyinfoProps> = ({ userInfo, onChangePassword }) => {
  const [localUserInfo, setLocalUserInfo] = useState<UserInfo | undefined>(
    userInfo,
  );
  const { t } = useTranslation();
  const [validationErrors, setValidationErrors] = useState({
    username: "",
    callingCode: "",
    phoneNumber: "",
    email: "",
  });
  // Mutation for updating user information
  const mutation = useMutation(
    async (data?: UserInfo) => {
      if (!data) throw new Error("User info is not available");
      return await UserService.updateUserInfo(data);
    },
    {
      onSuccess: (data) => {
        console.log("Update successful:", data);
      },
      onError: (error: any) => {
        if (error.code === 1019) {
          setValidationErrors({
            ...validationErrors,
            username: t("The username is already taken"),
          });
        } else {
          setValidationErrors({
            ...validationErrors,
            username: error.message,
          });
        }
      },
    },
  );
  const validateForm = () => {
    if (!localUserInfo) return false;

    let errors: any = {};
    if (!localUserInfo.username) {
      errors.username = t("Username is required");
    }

    if (!localUserInfo.phoneNumber || !localUserInfo.callingCode) {
      errors.phoneNumber = t("Phone number is required");
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    // Sync local state when userInfo prop changes (e.g., after successful save)
    setLocalUserInfo(userInfo);
  }, [userInfo]);

  const handleUpdate = () => {
    if (localUserInfo && validateForm()) {
      mutation.mutate(localUserInfo);
    }
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (localUserInfo) {
      setLocalUserInfo({
        ...localUserInfo,
        username: e.target.value,
      });
    }
  };
  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (localUserInfo) {
      setLocalUserInfo({
        ...localUserInfo,
        phoneNumber: e.target.value,
      });
    }
  };

  const handleCallingCode = (value: string) => {
    console.log("calling code", value);
    if (localUserInfo) {
      setLocalUserInfo({
        ...localUserInfo,
        callingCode: value,
      });
    }
  };

  return (
    <div>
      <Header onSave={handleUpdate} isLoading={mutation.isLoading} />
      <InfoSection
        userInfo={localUserInfo}
        validationErrors={validationErrors}
        onChangePassword={onChangePassword}
        onUsernameChange={handleUsernameChange}
        onChangePhone={handlePhoneNumberChange}
        onChangeCallingCode={handleCallingCode}
      />
    </div>
  );
};

export default Myinfo;

// ----- Header Component -----
interface HeaderProps {
  onSave: () => void;
  isLoading: boolean;
}

const Header: React.FC<HeaderProps> = ({ onSave, isLoading }) => {
  const { t } = useTranslation();
  return (
    <div className="mb-6 flex justify-between">
      <UiH4 text={t("My information")} />
      <button
        onClick={onSave}
        type="button"
        disabled={isLoading}
        className="font-noto-sans w-[7.8125rem] rounded-[3.125rem] border border-[#232323] bg-black py-[0.9375rem] text-center text-base font-semibold leading-5 text-white focus:ring-gray-200"
      >
        {isLoading ? t("Saving...") : t("Save")}
      </button>
    </div>
  );
};

// ----- Info Section Component -----
interface InfoSectionProps {
  userInfo?: UserInfo;
  error?: string;
  validationErrors: any;
  onChangePassword: () => void;
  onUsernameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangePhone: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeCallingCode: (value: string) => void;
}

const InfoSection: React.FC<InfoSectionProps> = ({
  userInfo,
  error,
  validationErrors,
  onChangePassword,
  onUsernameChange,
  onChangePhone,
  onChangeCallingCode,
}) => {
  const { t } = useTranslation();
  return (
    <div className="rounded-[1.25rem] border border-[#DCDCDC] p-[1.25rem] pt-[1.3125rem]">
      <div className="flex flex-col gap-[1.25rem]">
        {/* Email Section */}
        <InputField
          label={t("Email")}
          value={userInfo?.email}
          isDisabled={true}
        />

        {/* Username Section */}
        <div className="flex flex-col gap-[0.625rem]">
          <p className="text-[#232323]">{t("Username")}</p>
          <input
            value={userInfo?.username}
            onChange={onUsernameChange}
            type="text"
            className="onChangePhone: (e: React.ChangeEvent<HTMLInputElement>) => void; onChangeCallingCode: (value: string) => void;rounded-[0.625rem] h-[3.125rem] w-full rounded-[0.625rem] border border-gray-300 px-4 py-2 shadow-sm focus:border-[#DCDCDC] focus:outline-none"
          />
          {validationErrors.username && (
            <div className="mt-2 text-[0.75rem] text-[#DA3838]">
              {validationErrors.username}
            </div>
          )}
        </div>

        {/* Phone Number Section */}
        <PhoneNumberSection
          {...userInfo}
          onChangePhone={onChangePhone}
          error={validationErrors.phoneNumber}
          onChangeCallingCode={onChangeCallingCode}
        />

        {/* Password Section */}
        <PasswordSection onChangePassword={onChangePassword} />
      </div>
    </div>
  );
};

// ----- Input Field Component -----
interface InputFieldProps {
  label: string;
  value?: string;
  isDisabled?: boolean;
}

const InputField: React.FC<InputFieldProps> = ({
  label,
  value = "",
  isDisabled = false,
}) => (
  <div className="flex flex-col gap-[0.625rem]">
    <p className="text-[#232323]">{label}</p>
    <input
      value={value}
      disabled={isDisabled}
      type="text"
      className="h-[3.125rem] w-full rounded-[0.625rem] border border-gray-300 px-4 py-2 shadow-sm focus:border-[#DCDCDC] focus:outline-none"
    />
  </div>
);

// ----- Password Section -----
interface PasswordSectionProps {
  onChangePassword: () => void;
}

const PasswordSection: React.FC<PasswordSectionProps> = ({
  onChangePassword,
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-[0.625rem]">
      <p className="text-[#232323]">{t("Password")}</p>
      <div className="flex justify-between gap-[0.63rem]">
        <input
          type="text"
          value="************"
          readOnly
          className="h-[3.125rem] w-full rounded-[0.625rem] border border-gray-300 px-4 py-2 shadow-sm focus:border-[#DCDCDC] focus:outline-none"
        />
        <button
          onClick={onChangePassword}
          className="flex h-[3.13rem] min-w-[7.75rem] items-center justify-center rounded-[1.5rem] bg-blue-50 px-[2.13rem] text-center text-base font-semibold text-[#007AFF]"
        >
          {t("Change")}
        </button>
      </div>
    </div>
  );
};
